import React, {useEffect} from 'react'
import {BrowserRouter as Router, Routes, Route} from 'react-router-dom'
import MainLayout from './components/MainLayout.tsx'
import ReleasesAndVersions from './views/ReleasesAndVersions.tsx'
import VersionPublisher from './views/VersionPublisher.tsx'
import BulkRating from './views/BulkRating.tsx'
import BulkRatingDetail from './views/BulkRatingDetail.tsx'
import ProposalRating from './views/ProposalRating.tsx'
import AccountSettings from './views/AccountSettings.tsx'
import {ThemeProvider, createTheme} from "@mui/material/styles";
import { theme as origamiTheme } from "@origamirisk/origami-storybook";
import {AccountProvider} from './context/AccountContext.tsx'
import {AuthProvider, useAuth} from './context/AuthContext.tsx'
import './styles/ag-grid-custom.css'

const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'

const customTheme = createTheme({
    ...origamiTheme,
    typography: {
        ...origamiTheme.typography,
        fontFamily,
        h1: { ...origamiTheme.typography?.h1, fontFamily },
        h2: { ...origamiTheme.typography?.h2, fontFamily },
        h3: { ...origamiTheme.typography?.h3, fontFamily },
        h4: { ...origamiTheme.typography?.h4, fontFamily },
        h5: { ...origamiTheme.typography?.h5, fontFamily },
        h6: { ...origamiTheme.typography?.h6, fontFamily },
        body1: { ...origamiTheme.typography?.body1, fontFamily },
        body2: { ...origamiTheme.typography?.body2, fontFamily },
        subtitle1: { ...origamiTheme.typography?.subtitle1, fontFamily },
        subtitle2: { ...origamiTheme.typography?.subtitle2, fontFamily },
        button: { ...origamiTheme.typography?.button, fontFamily },
        caption: { ...origamiTheme.typography?.caption, fontFamily },
        overline: { ...origamiTheme.typography?.overline, fontFamily }
    },
    components: {
        MuiSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: 'white'
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    backgroundColor: 'white'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: 'white'
                }
            }
        }
    }
})

function AuthGuard({children}: {children: React.ReactNode}) {
    const {isAuthenticated, isLoading, authUrl} = useAuth()

    // Redirect to auth URL when not authenticated (side effect in useEffect to prevent issues with StrictMode)
    useEffect(() => {
        if (!isLoading && !isAuthenticated && authUrl) {
            console.log('[AuthGuard] Redirecting to:', authUrl)
            window.location.href = authUrl
        }
    }, [isLoading, isAuthenticated, authUrl])

    if (isLoading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <div>Loading...</div>
            </div>
        )
    }

    if (!isAuthenticated) {
        // Redirect to auth URL if available, otherwise show message
        if (authUrl) {
            return (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                    <div>Redirecting to authentication...</div>
                </div>
            )
        }
        
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column'}}>
                <h1>Unauthorized</h1>
                <p>Please provide a valid token to access this application.</p>
            </div>
        )
    }

    return <>{children}</>
}

function App() {
    return (
        <ThemeProvider theme={customTheme}>
            <AuthProvider>
                <AuthGuard>
                    <AccountProvider>
                        <Router>
                            <Routes>
                                <Route path="/" element={<MainLayout/>}>
                                    <Route index element={<ReleasesAndVersions/>}/>
                                    <Route path="version-publisher" element={<VersionPublisher/>}/>
                                    <Route path="bulk-rating" element={<BulkRating/>}/>
                                    <Route path="bulk-rating/:requestId" element={<BulkRatingDetail/>}/>
                                    <Route path="proposal-rating" element={<ProposalRating/>}/>
                                    <Route path="account-settings" element={<AccountSettings/>}/>
                                </Route>
                            </Routes>
                        </Router>
                    </AccountProvider>
                </AuthGuard>
            </AuthProvider>
        </ThemeProvider>
    )
}

export default App
